import { useField, useFormikContext } from 'formik';
import { parseInt } from 'lodash';
import moment from 'moment';
import { ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { LocalizationScope, useLocalize } from '@treams/treams-hooks';
import { flags } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';

import { DatePicker } from '../../common/DatePicker';
import { Flag } from '../../common/Flag';
import { withFormik } from '../../common/Formik';
import { LanguageSelect } from '../../common/LanguageSelect';
import { Popover } from '../../common/Popover';
import { Select } from '../../common/Select';
import { Skeleton } from '../../common/Skeleton';
import { Spinner } from '../../common/Spinner';
import { CalendarIcon, CloseCircleIcon } from '../../icons';
import { tokens } from '../../theme/tokens';
import { FieldType } from './Field';
import {
	FieldStyleProps,
	INPUT_ICON_GAP,
	InputDescription,
	InputError,
	InputGroup,
	InputLabel,
	fieldStyle,
} from './fieldStyle';

export { fieldStyle } from './fieldStyle';
export type { FieldStyleProps as FieldProps } from './fieldStyle';

const WrapperBox = styled(Box)`
	position: relative;

	&::after {
		content: attr(data-suffix);
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		right: 10px;
		pointer-events: none;
		color: ${tokens.text.tertiary};
		font-size: 14px;
	}

	&:has(input[type='number']) {
		&::after {
			transition: right 0.1s ease-out;
		}

		&:has(input:focus, input:hover)::after {
			right: 30px;
		}
	}
`;

const Field = styled.input<FieldStyleProps>`
	${fieldStyle}
`;

const Icon = styled(Flex)<{ tinted?: boolean }>`
	position: absolute;
	top: 50%;
	left: ${tokens.padding.control}px;
	transform: translateY(-50%);
	pointer-events: none;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	color: ${(props) => (props.tinted ? tokens.icon.tertiary : tokens.icon.primary)};

	> svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}
`;

const Suggestion = styled(Box)`
	position: absolute;
	top: 0px;
	left: 0px;
	padding: ${tokens.padding.compact}px ${tokens.padding.control + 16 + INPUT_ICON_GAP}px;
	pointer-events: none;
	color: ${tokens.text.tertiary};
	font-size: ${tokens.typography.sizes.label}px;
	line-height: 17px;
`;

const Hidden = styled.span`
	color: transparent;
`;

const SpinnerContainer = styled(Box)`
	position: absolute;
	right: ${tokens.padding.control}px;
	top: 50%;
	transform: translateY(-50%);
`;

const ClearButton = styled.button`
	position: absolute;
	right: ${tokens.padding.control}px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	padding: 0;
	border: none;
	background: none;
	color: ${tokens.icon.tertiary};
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
	}

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}
`;

interface IInputProps extends BoxProps {
	icon?: ReactNode;
	label?: string;
	id?: string;
	name?: string;
	tintedIcon?: boolean;
	value?: string;
	loading?: boolean;
	valid?: boolean;
	error?: string;
	/** @deprecated The New UI Input is a single size. */
	small?: boolean;
	search?: boolean;
	disabled?: boolean;
	focus?: boolean;
	type?: string;
	onFocus?: () => void;
	onBlur?: () => void;
	onChange: (value: string) => void;
	onKeyDown?: React.KeyboardEventHandler;
	completion?: string;
	autoFocus?: boolean;
	placeholder?: string;
	autoComplete?: string;
	mergeWith?: 'top' | 'right' | 'bottom' | 'left';
	alternativeStyle?: 'enabled' | 'focused';
	inputRef?: React.RefObject<HTMLInputElement>;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	suffix?: string;
	description?: ReactNode;
	clearable?: boolean;
	onClear?: () => void;
	'data-preview'?: 'hover' | 'focus';
}

export function Input(props: BoxProps & IInputProps) {
	const {
		icon,
		label,
		id,
		value,
		small: _small,
		search,
		type,
		error,
		valid,
		onFocus,
		onBlur,
		onChange,
		onKeyDown,
		completion,
		autoFocus,
		placeholder,
		autoComplete,
		mergeWith,
		alternativeStyle,
		loading,
		inputRef,
		suffix,
		description,
		clearable,
		onClear,
		['data-preview']: dataPreview,
		...rest
	} = props;
	void _small;

	const { t } = useTranslation();
	const generatedId = useId();
	const inputId = id ?? props.inputProps?.id ?? generatedId;

	const field = (
		<WrapperBox style={{ width: '100%' }} data-suffix={suffix}>
			{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
			<Box {...(rest as any)} style={{ position: 'relative' }}>
				{icon && <Icon tinted={props.tintedIcon ?? true}>{icon}</Icon>}
				{loading && (
					<SpinnerContainer>
						<Spinner />
					</SpinnerContainer>
				)}
				{clearable && !loading && (
					<ClearButton
						type="button"
						disabled={props.disabled}
						aria-label="Clear"
						onClick={() => {
							onChange('');
							onClear?.();
						}}
					>
						<CloseCircleIcon size={16} solid />
					</ClearButton>
				)}
				<Field
					autoFocus={autoFocus}
					icon={!!icon}
					search={search}
					type={type}
					value={value}
					placeholder={placeholder}
					onFocus={onFocus}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					autoComplete={autoComplete}
					valid={valid}
					error={error}
					disabled={props.disabled}
					$filled={Boolean(value)}
					$trailing={clearable && !loading}
					data-preview={dataPreview}
					onChange={(event) => onChange(event.currentTarget.value)}
					mergeWith={mergeWith}
					alternativeStyle={alternativeStyle}
					{...props.inputProps}
					id={inputId}
					ref={inputRef}
				/>
				{completion && completion.toLowerCase().startsWith((value ?? '').toLowerCase()) && (
					<Suggestion>
						<Hidden>{value}</Hidden>
						{completion.substring(value?.length ?? 0)}
					</Suggestion>
				)}
			</Box>
		</WrapperBox>
	);

	if (!label && !description && !error) {
		return field;
	}

	return (
		<InputGroup>
			{label && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
			{field}
			{error ? (
				<InputError>{t(error)}</InputError>
			) : description ? (
				<InputDescription>{description}</InputDescription>
			) : null}
		</InputGroup>
	);
}

Input.Small = InputError;

export function NumericInput(
	props: BoxProps & IInputProps & { prefix?: string; onChange?: (value?: string) => void },
) {
	const intValue = parseInt(props.value ?? '');
	const valueString =
		props.value === undefined ? '' : isNaN(intValue) ? 0 : intValue.toLocaleString('nl-NL');
	const prefix = `${props.prefix ? `${props.prefix} ` : ''}`;

	return (
		<Box style={{ position: 'relative' }} flexGrow={1}>
			<Input
				{...props}
				value={`${prefix}${valueString}`}
				onChange={(value) => {
					const re = /^[0-9\b]+$/;
					const strippedValue = value.replace(prefix, '').replace(/[.]/g, '');

					if (strippedValue === '') {
						props.onChange(undefined);
						return;
					}

					if (re.test(strippedValue)) {
						props.onChange(strippedValue);
					}
				}}
			/>
		</Box>
	);
}

const FormikElement = withFormik(Input);

Input.Formik = function InputFormik(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & { validate?: boolean; name: string },
) {
	const form = useFormikContext();
	const [, meta] = useField(props.name);

	return (
		<FormikElement
			{...props}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					form.submitForm();
					event.preventDefault();
					return;
				}
			}}
			error={props.error && meta.error}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
};

const InputSkeleton = styled(Skeleton.Box)<{ search?: boolean }>`
	height: 33px;
	border-radius: ${tokens.radius.sm}px;
`;

Input.Skeleton = InputSkeleton;

function InputDatePicker(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & {
		value?: Date;
		onChange: (date?: Date) => void;
	},
) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(props.autoFocus ?? false);

	return (
		<Popover.Target
			visible={visible}
			contents={
				<DatePicker
					value={props.value}
					onChange={props.onChange}
					onDismiss={() => setVisible(false)}
				/>
			}
			onDismiss={() => setVisible(false)}
		>
			<Box tabIndex={0} onFocus={() => (props.disabled ? undefined : setVisible(true))}>
				<Input
					{...props}
					disabled
					icon={<CalendarIcon />}
					value={props.value ? moment(props.value).format('L') : t('common.date.empty')}
					alternativeStyle={props.disabled ? undefined : visible ? 'focused' : 'enabled'}
					onChange={() => {}}
				/>
			</Box>
		</Popover.Target>
	);
}

Input.DatePicker = InputDatePicker;

const InputDatePickerFormikElement = withFormik(InputDatePicker);

function InputDatePickerFormik(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & { name: string; validate?: boolean },
) {
	const [, meta] = useField(props.name);

	return (
		<InputDatePickerFormikElement
			{...props}
			error={props.error && meta.error}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
}

InputDatePicker.Formik = InputDatePickerFormik;

function InputLanguage(props: IInputProps) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const [fetch, query] = graphql.useSupportedLanguageIDsLazyQuery();

	return (
		<Popover.Target
			visible={visible}
			contents={
				<Select.Menu
					tint={false}
					search={false}
					selectedValue={props.value}
					options={query.data?.viewer.workspace.supportedLanguageIDs.map((languageID) => ({
						icon: <Flag languageID={languageID as keyof typeof flags} />,
						title: t('common.language.' + languageID),
						value: languageID,
					}))}
					onChange={(value) => {
						props.onChange(value!);
						setVisible(false);
					}}
				/>
			}
			onDismiss={() => setVisible(false)}
		>
			<Box
				tabIndex={0}
				onFocus={() => {
					if (props.disabled) {
						return;
					}

					setVisible(true);
					fetch();
				}}
			>
				<Input
					{...props}
					disabled
					tintedIcon={false}
					value={t('common.language.' + props.value)}
					icon={<Flag languageID={props.value as keyof typeof flags} />}
					alternativeStyle={props.disabled ? undefined : visible ? 'focused' : 'enabled'}
					onChange={() => {}}
				/>
			</Box>
		</Popover.Target>
	);
}

Input.Language = InputLanguage;

const InputLanguageFormikElement = withFormik(InputLanguage);

function InputLanguageFormik(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & { name: string; validate?: boolean },
) {
	const [, meta] = useField(props.name);

	return (
		<InputLanguageFormikElement
			{...props}
			error={props.error && meta.error}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
}

InputLanguage.Formik = InputLanguageFormik;

const Container = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 10px;
	min-width: 20rem;
`;

const CustomField = styled.div<{ isFocused: boolean }>`
	width: 100%;
	display: flex;
	overflow: hidden;
	align-items: center;
	box-sizing: border-box;
	min-height: 33px;
	background: ${tokens.surface.white};
	border: 1px solid ${(props) => (props.isFocused ? tokens.border.focus : tokens.border.input)};
	border-radius: ${tokens.radius.sm}px;
	box-shadow: ${tokens.shadow.input};
`;

const BlankField = styled(Input)`
	height: 100%;
	margin-right: 10px;

	input {
		height: 33px;
		background: transparent;
		border: none;
		box-shadow: none;

		&:hover,
		&:focus {
			background: transparent;
			border: none;
			box-shadow: none;
		}
	}
`;

function MultilingualInput(props: { name: string; 'aria-label'?: string }) {
	const { language } = useLocalize();

	const [field, , { setValue }] = useField<graphql.LocalizedString>(props.name);
	const [localValue, setLocalValue] = useState(field.value[0]?.text ?? '');

	const [isFocused, setIsFocused] = useState(false);

	const languageIndex = field.value.findIndex((item) => item.languageID === language.id);

	useEffect(() => {
		setLocalValue(languageIndex === -1 ? '' : field.value[languageIndex].text);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [languageIndex]);

	const onChange = async (value: string) => {
		setLocalValue(value);

		if (languageIndex === -1) {
			await setValue([
				...field.value,
				{
					languageID: language.id,
					text: value,
				},
			]);
			return;
		}

		if (value === '') {
			const array = [
				...field.value.slice(0, languageIndex),
				...field.value.slice(languageIndex + 1),
			];

			await setValue(array);
			return;
		}

		const array = [...field.value];
		array[languageIndex] = {
			...field.value[languageIndex],
			text: value,
		};

		await setValue(array);
	};

	return (
		<CustomField isFocused={isFocused}>
			<Box width="calc(100% - 155px)">
				<BlankField
					type={FieldType.Text}
					value={localValue}
					inputProps={props['aria-label'] ? { 'aria-label': props['aria-label'] } : undefined}
					onFocus={() => setIsFocused(true)}
					onChange={(value) => onChange(value)}
					onBlur={() => setIsFocused(false)}
				/>
			</Box>
			<Flex width="155px" justifyContent="flex-end">
				<LanguageSelect.Scope />
			</Flex>
		</CustomField>
	);
}

function InputLocalizable(props: { name: string; 'aria-label'?: string }) {
	return (
		<LocalizationScope>
			<Container>
				<MultilingualInput name={props.name} aria-label={props['aria-label']} />
			</Container>
		</LocalizationScope>
	);
}

Input.Localizable = InputLocalizable;
